import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useOrg } from './useOrg'

const DEFAULT_MODULES = ['dashboard', 'animals', 'hours', 'finances', 'inventory', 'agreements', 'stripe']
const REFRESH_EVENT = 'placed:modules-refresh'

export function useModules() {
  const { orgId } = useOrg()
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchModules = useCallback(async () => {
    if (!orgId) {
      setModules(DEFAULT_MODULES)
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('org_modules')
      .select('module_name, enabled')
      .eq('org_id', orgId)

    if (error || !data?.length) {
      setModules(DEFAULT_MODULES)
    } else {
      setModules(data.filter(m => m.enabled).map(m => m.module_name))
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => { fetchModules() }, [fetchModules])

  // Cross-component refresh: any caller of refreshModules() (e.g. the
  // Platform OrgDetailPage after toggling a module) fires this event so
  // every Sidebar / BottomNav re-reads org_modules immediately instead
  // of waiting for a full reload.
  useEffect(() => {
    function onRefresh() { fetchModules() }
    window.addEventListener(REFRESH_EVENT, onRefresh)
    return () => window.removeEventListener(REFRESH_EVENT, onRefresh)
  }, [fetchModules])

  function isEnabled(moduleName) {
    return modules.includes(moduleName)
  }

  return { modules, loading, isEnabled, refreshModules: fetchModules }
}

export function broadcastModulesRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(REFRESH_EVENT))
  }
}
